import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Users, Search, RefreshCw, Download, AlertTriangle, TrendingUp,
  CheckCircle2, XCircle, Eye, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  useUserTrackingList,
  useUserTrackingStats,
  UserTrackingFilters,
  UserTrackingRow,
  LicenseCode,
} from '@/hooks/useUserTracking';
import { UserTrackingDetailModal } from '@/components/admin/tracking/UserTrackingDetailModal';

export default function UserTrackingPage() {
  const { currentRole } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSuperAdmin = currentRole === 'super_admin';

  // Filters state
  const [search, setSearch] = useState('');
  const [orgId, setOrgId] = useState<string | null>(null);
  const [cohortId, setCohortId] = useState<string | null>(null);
  const [onlyActiveCohorts, setOnlyActiveCohorts] = useState(true);
  const [onlyWithActivity, setOnlyWithActivity] = useState(true);
  const [sortBy, setSortBy] = useState<UserTrackingFilters['sortBy']>('last_activity');
  const [sortDir, setSortDir] = useState<UserTrackingFilters['sortDir']>('desc');
  const [licenseType, setLicenseType] = useState<LicenseCode>(null);
  const [activityFrom, setActivityFrom] = useState<string>('');
  const [activityTo, setActivityTo] = useState<string>('');
  const [hasProject, setHasProject] = useState<'all' | 'yes' | 'no'>('all');
  const [hasDeliverables, setHasDeliverables] = useState<'all' | 'yes' | 'no'>('all');
  const [hasCohort, setHasCohort] = useState<'all' | 'yes' | 'no'>('all');

  const triToBool = (v: 'all' | 'yes' | 'no') => v === 'yes' ? true : v === 'no' ? false : null;
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(
    searchParams.get('user'),
  );

  // Selectors de organizations y cohorts (para dropdowns)
  const { data: organizations = [] } = useQuery({
    queryKey: ['organizations-list-for-tracking'],
    queryFn: async () => {
      const { data } = await supabase.from('organizations').select('id, name').order('name');
      return (data ?? []) as { id: string; name: string }[];
    },
    enabled: isSuperAdmin,
  });

  const { data: cohorts = [] } = useQuery({
    queryKey: ['cohorts-list-for-tracking', orgId],
    queryFn: async () => {
      let q = supabase.from('cohorts').select('id, name, organization_id, end_date').order('start_date', { ascending: false });
      if (orgId) q = q.eq('organization_id', orgId);
      const { data } = await q;
      return (data ?? []) as { id: string; name: string; organization_id: string | null; end_date: string | null }[];
    },
  });

  const commonFilters = {
    orgId,
    cohortId,
    onlyActiveCohorts,
    onlyWithActivity,
    search,
    licenseType,
    activityFrom: activityFrom || null,
    activityTo: activityTo || null,
    hasProject: triToBool(hasProject),
    hasDeliverables: triToBool(hasDeliverables),
    hasCohort: triToBool(hasCohort),
  };

  const { data: listData, isLoading, refetch } = useUserTrackingList({
    ...commonFilters,
    sortBy,
    sortDir,
    page,
    pageSize,
  });

  // Stats globales del filtro (agregados sobre TODOS los users, no solo la página)
  const { data: statsData } = useUserTrackingStats(commonFilters);

  const rows = listData?.rows ?? [];
  const totalCount = Number(listData?.totalCount ?? 0);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const kpis = {
    total: statsData?.total ?? totalCount,
    avgContent: statsData?.avg_content_percent ?? 0,
    withProject: statsData?.with_project ?? 0,
    diagCompleted: statsData?.diagnostic_completed ?? 0,
    atRisk: statsData?.at_risk ?? 0,
  };

  const handleExport = () => {
    if (!rows.length) return;
    const exportRows = rows.map(r => ({
      Nombre: r.full_name ?? '',
      Email: r.email ?? '',
      Organización: r.organization_name ?? '',
      'Cohorte principal': r.primary_cohort_name ?? '',
      Cohortes: r.cohorts_count,
      Licencia: r.license_code ?? 'free',
      'Progreso contenido (%)': r.content_percent,
      'Entregables enviados': r.deliverables_submitted,
      'Entregables total': r.deliverables_total,
      'Proyectos creados': r.projects_created,
      'Proyectos enviados': r.projects_submitted,
      'Soluciones Lab': r.challenge_solutions_count,
      'Diagnóstico completado': r.diagnostic_completed ? 'Sí' : 'No',
      'Última actividad': r.last_activity_at ?? '',
      'En riesgo': r.is_at_risk ? 'Sí' : 'No',
    }));
    const ws = XLSX.utils.json_to_sheet(exportRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Seguimiento');
    const timestamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `seguimiento_usuarios_${timestamp}.xlsx`);
  };

  const openDetail = (userId: string) => {
    setSelectedUserId(userId);
    const next = new URLSearchParams(searchParams);
    next.set('user', userId);
    setSearchParams(next, { replace: true });
  };

  const closeDetail = () => {
    setSelectedUserId(null);
    const next = new URLSearchParams(searchParams);
    next.delete('user');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="container mx-auto py-8 max-w-[1400px] space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" />
            Seguimiento de Usuarios
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Vista transversal por participante — progreso, entregables, proyectos y actividad.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refrescar
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={rows.length === 0}>
            <Download className="w-4 h-4 mr-2" />
            Exportar Excel
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KpiCard label="Total en filtro" value={String(totalCount)} icon={<Users className="w-4 h-4" />} />
        <KpiCard label="Progreso promedio" value={`${kpis.avgContent}%`} icon={<TrendingUp className="w-4 h-4" />} tone="emerald" />
        <KpiCard label="Con proyecto" value={String(kpis.withProject)} tone="cyan" />
        <KpiCard label="Diagnóstico OK" value={String(kpis.diagCompleted)} tone="blue" />
        <KpiCard label="En riesgo" value={String(kpis.atRisk)} icon={<AlertTriangle className="w-4 h-4" />} tone="destructive" />
      </div>

      {/* Filtros */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Filtros</CardTitle>
          <CardDescription>Búsqueda y filtros del conjunto</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="relative md:col-span-2">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre o email..."
                className="pl-9"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
            {isSuperAdmin && (
              <Select
                value={orgId ?? 'all'}
                onValueChange={(v) => { setOrgId(v === 'all' ? null : v); setCohortId(null); setPage(1); }}
              >
                <SelectTrigger><SelectValue placeholder="Organización" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las organizaciones</SelectItem>
                  {organizations.map(o => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select
              value={cohortId ?? 'all'}
              onValueChange={(v) => { setCohortId(v === 'all' ? null : v); setPage(1); }}
            >
              <SelectTrigger><SelectValue placeholder="Cohorte" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las cohortes</SelectItem>
                {cohorts.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
            <Select
              value={licenseType ?? 'all'}
              onValueChange={(v) => { setLicenseType(v === 'all' ? null : (v as LicenseCode)); setPage(1); }}
            >
              <SelectTrigger><SelectValue placeholder="Tipo de licencia" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las licencias</SelectItem>
                <SelectItem value="free">Free</SelectItem>
                <SelectItem value="starter">Starter</SelectItem>
                <SelectItem value="pro">Pro</SelectItem>
                <SelectItem value="enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>

            <Select value={hasProject} onValueChange={(v) => { setHasProject(v as any); setPage(1); }}>
              <SelectTrigger><SelectValue placeholder="¿Tiene proyecto?" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Proyecto: cualquiera</SelectItem>
                <SelectItem value="yes">Solo con proyecto</SelectItem>
                <SelectItem value="no">Sin proyecto</SelectItem>
              </SelectContent>
            </Select>

            <Select value={hasDeliverables} onValueChange={(v) => { setHasDeliverables(v as any); setPage(1); }}>
              <SelectTrigger><SelectValue placeholder="¿Tiene entregables?" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Entregables: cualquiera</SelectItem>
                <SelectItem value="yes">Solo con entregables</SelectItem>
                <SelectItem value="no">Sin entregables</SelectItem>
              </SelectContent>
            </Select>

            <Select value={hasCohort} onValueChange={(v) => { setHasCohort(v as any); setPage(1); }}>
              <SelectTrigger><SelectValue placeholder="¿Asignado a cohorte?" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Cohorte: cualquiera</SelectItem>
                <SelectItem value="yes">Con cohorte asignada</SelectItem>
                <SelectItem value="no">Sin cohorte (cortesía)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
            <div className="md:col-span-2 flex items-center gap-2">
              <Label className="text-sm whitespace-nowrap">Actividad:</Label>
              <Input
                type="date"
                value={activityFrom}
                onChange={(e) => { setActivityFrom(e.target.value); setPage(1); }}
                className="flex-1"
                placeholder="Desde"
              />
              <span className="text-muted-foreground text-sm">a</span>
              <Input
                type="date"
                value={activityTo}
                onChange={(e) => { setActivityTo(e.target.value); setPage(1); }}
                className="flex-1"
                placeholder="Hasta"
              />
              {(activityFrom || activityTo) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setActivityFrom(''); setActivityTo(''); setPage(1); }}
                >
                  ✕
                </Button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-4 mt-4">
            <div className="flex items-center gap-2">
              <Switch
                id="active-cohorts"
                checked={onlyActiveCohorts}
                onCheckedChange={(v) => { setOnlyActiveCohorts(v); setPage(1); }}
              />
              <Label htmlFor="active-cohorts" className="text-sm cursor-pointer">Solo cohortes activas</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="with-activity"
                checked={onlyWithActivity}
                onCheckedChange={(v) => { setOnlyWithActivity(v); setPage(1); }}
              />
              <Label htmlFor="with-activity" className="text-sm cursor-pointer">Solo con actividad</Label>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <Label className="text-sm">Ordenar:</Label>
              <Select value={sortBy} onValueChange={(v: any) => setSortBy(v)}>
                <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="last_activity">Última actividad</SelectItem>
                  <SelectItem value="name">Nombre</SelectItem>
                  <SelectItem value="content_percent">Progreso contenido</SelectItem>
                  <SelectItem value="deliverables">Entregables</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortDir} onValueChange={(v: any) => setSortDir(v)}>
                <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="desc">↓ Desc</SelectItem>
                  <SelectItem value="asc">↑ Asc</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabla */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Participantes ({totalCount})</span>
            {totalPages > 1 && (
              <div className="flex items-center gap-2 text-sm font-normal">
                <span className="text-muted-foreground">
                  Página {page} de {totalPages}
                </span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1,2,3,4,5,6,7,8].map(i => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : rows.length === 0 ? (
            <p className="text-center text-muted-foreground py-12">
              Sin participantes que coincidan con los filtros.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Participante</TableHead>
                    <TableHead>Cohorte principal</TableHead>
                    <TableHead>Licencia</TableHead>
                    <TableHead>Contenido</TableHead>
                    <TableHead className="text-center">Entregables</TableHead>
                    <TableHead className="text-center">Proyectos</TableHead>
                    <TableHead className="text-center">Lab</TableHead>
                    <TableHead className="text-center">Diagnóstico</TableHead>
                    <TableHead>Última actividad</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(r => (
                    <TableRow key={r.user_id} className="cursor-pointer hover:bg-muted/50" onClick={() => openDetail(r.user_id)}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{r.full_name ?? '—'}</p>
                          <p className="text-xs text-muted-foreground">{r.email}</p>
                          {isSuperAdmin && r.organization_name && (
                            <p className="text-xs text-muted-foreground">{r.organization_name}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {r.primary_cohort_name ?? <span className="text-muted-foreground">Sin cohorte</span>}
                        {r.cohorts_count > 1 && (
                          <Badge variant="secondary" className="ml-1 text-[10px]">+{r.cohorts_count - 1}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <LicenseBadge code={r.license_code} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 min-w-[100px]">
                          <Progress value={r.content_percent} className="h-2 flex-1" />
                          <span className="text-xs w-10 text-right">{r.content_percent}%</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {r.deliverables_submitted}/{r.deliverables_total}
                      </TableCell>
                      <TableCell className="text-center text-sm">
                        {r.projects_submitted}/{r.projects_created}
                      </TableCell>
                      <TableCell className="text-center text-sm">{r.challenge_solutions_count}</TableCell>
                      <TableCell className="text-center">
                        {r.diagnostic_completed
                          ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" />
                          : <XCircle className="w-4 h-4 text-muted-foreground mx-auto" />}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {r.last_activity_at
                          ? formatDistanceToNow(parseISO(r.last_activity_at), { locale: es, addSuffix: true })
                          : <span className="italic">Sin actividad</span>}
                      </TableCell>
                      <TableCell>
                        {r.is_at_risk
                          ? <Badge className="bg-destructive/20 text-destructive border-destructive/30">En riesgo</Badge>
                          : <Badge variant="secondary" className="opacity-60">OK</Badge>}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openDetail(r.user_id); }}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <UserTrackingDetailModal userId={selectedUserId} onClose={closeDetail} />
    </div>
  );
}

const LICENSE_STYLE: Record<string, { label: string; className: string }> = {
  free:       { label: 'Free',       className: 'bg-slate-500/20 text-slate-500 border-slate-500/30' },
  starter:    { label: 'Starter',    className: 'bg-blue-500/20 text-blue-500 border-blue-500/30' },
  pro:        { label: 'Pro',        className: 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30' },
  enterprise: { label: 'Enterprise', className: 'bg-purple-500/20 text-purple-500 border-purple-500/30' },
};

function LicenseBadge({ code }: { code: string | null | undefined }) {
  const key = (code ?? 'free').toLowerCase();
  const style = LICENSE_STYLE[key] ?? { label: key, className: 'bg-muted text-muted-foreground' };
  return <Badge className={style.className}>{style.label}</Badge>;
}

function KpiCard({ label, value, icon, tone }: { label: string; value: string; icon?: React.ReactNode; tone?: 'emerald' | 'destructive' | 'blue' | 'cyan' }) {
  const toneClass =
    tone === 'emerald' ? 'text-emerald-500' :
    tone === 'destructive' ? 'text-destructive' :
    tone === 'blue' ? 'text-blue-500' :
    tone === 'cyan' ? 'text-cyan-500' : '';
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          {icon}
          <span>{label}</span>
        </div>
        <p className={`text-2xl font-bold ${toneClass}`}>{value}</p>
      </CardContent>
    </Card>
  );
}
